// client/src/components/auth/ResetPassword.js - TRADOTTO
import React, { useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../layout/LanguageSelector';

// Security utilities minimali (solo protezioni critiche, non cambiano UX)
const Security = {
  limits: new Map(),
  
  // Rate limiting leggero (solo contro attacchi)
  checkReset(token) {
    if (!token) return true;
    const k = token.substring(0, 8);
    const now = Date.now();
    const l = this.limits.get(k) || { c: 0, t: now };
    if (now - l.t > 900000) { this.limits.set(k, { c: 0, t: now }); return true; }
    return l.c < 8; // Molto permissivo: 8 tentativi/15min
  },
  
  fail(token) {
    if (!token) return;
    const k = token.substring(0, 8);
    const l = this.limits.get(k) || { c: 0, t: Date.now() };
    l.c++; this.limits.set(k, l);
  },
  
  // Solo minacce CRITICHE (non interferisce con password normali)
  threat(input) {
    if (!input || typeof input !== 'string') return false;
    return [/<script[^>]*>/i, /<iframe[^>]*>/i, /javascript\s*:/i].some(p => p.test(input));
  },
  
  // Sanitizza SOLO messaggi server (non input utente)
  clean(msg) {
    if (!msg || typeof msg !== 'string') return '';
    return msg.replace(/<script[^>]*>.*?<\/script>/gi, '').trim().substring(0, 200);
  }
};

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // NUOVO: Solo per timing protection
  const lastSubmit = useRef(0);

  const { password, confirmPassword } = formData;

  // ORIGINALE: onChange identico (+ solo controllo minacce critiche)
  const onChange = e => {
    // NUOVO: Solo blocco minacce CRITICHE (non cambia UX normale)
    if (Security.threat(e.target.value)) {
      return; // Blocca solo <script>, <iframe>, javascript:
    }
    
    // ORIGINALE: Logica identica
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ORIGINALE: onSubmit con stessa logica + protezioni invisibili
  const onSubmit = async e => {
    e.preventDefault();
    setMessage('');
    setError('');

    // ORIGINALE: Check passwords match (identico)
    if (password !== confirmPassword) {
      return setError(t('auth.resetPassword.errors.passwordMismatch'));
    }

    // ORIGINALE: Password validation (identico)
    if (password.length < 10 || 
    !/[A-Z]/.test(password) || 
    !/[a-z]/.test(password) || 
    !/[0-9]/.test(password) || 
    !/[^A-Za-z0-9]/.test(password)) {
      return setError(t('auth.resetPassword.errors.passwordWeak'));
    }

    // NUOVO: Protezioni di sicurezza (non visibili all'utente normale)
    
    // Rate limiting molto permissivo
    if (!Security.checkReset(token)) {
      return setError(t('auth.resetPassword.errors.tooManyAttempts'));
    }
    
    // Timing protection minimo
    const now = Date.now();
    if (now - lastSubmit.current < 1000) {
      return setError(t('auth.resetPassword.errors.waitBefore'));
    }
    lastSubmit.current = now;

    // ORIGINALE: setLoading identico
    setLoading(true);

    try {
      // NUOVO: Log attempt per rate limiting
      Security.fail(token);
      
      // ORIGINALE: API call identica
      const res = await axios.post(`/api/auth/reset-password/${token}`, { password });
      
      // ORIGINALE: setMessage (+ sanitizzazione server response)
      const serverMessage = Security.clean(res.data.message);
      setMessage(t('auth.resetPassword.passwordResetSuccess'));
      setLoading(false);
      
      // ORIGINALE: Redirect identico
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      // ORIGINALE: Error handling identico (+ sanitizzazione server response)
      let errorMsg = err.response?.data?.message || t('auth.resetPassword.errors.resetFailed');
      errorMsg = Security.clean(errorMsg) || errorMsg;
      setError(errorMsg);
      setLoading(false);
    }
  };

  // ORIGINALE: Return JSX identico
  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card">
        <h2>{t('auth.resetPassword.title')}</h2>
        <p className="auth-subtitle">{t('auth.resetPassword.subtitle')}</p>
        
        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}
        
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="password">{t('auth.resetPassword.newPasswordLabel')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={onChange}
              required
              minLength="8"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPasswordLabel')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={onChange}
              required
              minLength="8"
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetButton')}
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            {t('auth.resetPassword.rememberPassword')}{' '}
            <Link to="/login">{t('auth.resetPassword.loginHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;